// Tenant-scoped workspace state. The provider is keyed by user+tenant, so a tenant
// switch or a different sign-in discards every cached call, contact and roster row.
import type { Agent, Call, Capability, Config, Device, EventEnvelope, Session, User } from '@bcio-voice/contracts';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { type ApiClient, type CollectionEnvelope, type DataEnvelope, isAbortError, isApiRequestError } from '../lib/api.ts';
import { EventStream, type StreamStatus } from '../lib/events.ts';
import { type ErrorView, describeError } from '../lib/errorCopy.ts';
import { capability, hasPermission } from '../lib/capabilities.ts';
import { type CallMap, CallProjectionOrder, type CallWithContext, decideEvent, isLive, liveCallsFor, upsertContextCard } from '../call/callStore.ts';
import { CommandController } from '../call/commands.ts';
import { type BridgeCall, type BridgeSnapshot, type CallBridge, type MicPermission, type PlaceCallRequest, UnconfiguredBridge } from '../call/bridge.ts';
import { type DeviceView, redactDevice } from '../call/deviceView.ts';
import { type StatusView, isPending, liveEngineCall, runAction, statusFor } from '../call/callControl.ts';
import type { ActionName } from '../call/commands.ts';
import { type Readiness, mergeAgentsProjection, phoneReadiness } from '../call/gating.ts';
import { CLIENT_VERSION } from '../config/phone.ts';
import { engineErrorText } from '../lib/userCopy.ts';
import type { DestinationDraft } from '../call/destination.ts';
import { useAnnouncer } from '../ui/Announcer.tsx';
import { createControlHandler, MediaAccess, type MediaAccessSnapshot } from './mediaAccess.ts';
import { CaptureController, CaptureRegistry } from '../call/capture.ts';
import { useDesktop } from '../desktop/bridge.ts';
import type { LocalCaptureStore } from '../call/desktopCapture.ts';
import { useDesktopCapture } from '../call/useDesktopCapture.ts';
import {type HistoryFilter,useCallHistory} from '../features/inbox/historyQuery.ts';
import { iphoneSafari, iphoneHomeScreen, onFirstPhoneGesture } from '../call/iphone.ts';
import { browserPhoneOwnership, type OwnershipSnapshot } from '../call/phoneOwnership.ts';
import { notifyTranscriptEvent, notifyTranscriptResync } from '../features/transcript/liveTranscript.ts';
export type {HistoryFilter} from '../features/inbox/historyQuery.ts';

export interface PhoneNumber {
  id: string;
  e164: string;
  label: string;
  status: string;
  capabilities: { voice_in: boolean; voice_out: boolean; sms: boolean; mms: boolean };
  routing_version: number;
  recording_policy?: import('@bcio-voice/contracts').NumberRecordingPolicy;
  recording_disclosure?: {inbound:string;outbound:string};
  assigned_to_user?: boolean;
  assigned_user_count?: number;
}

export interface Loadable<T> {
  value: T | null;
  error: ErrorView | null;
  loading: boolean;
}

export interface WorkspaceValue {
  api: ApiClient;
  session: Session;
  userId: string;
  permissions: readonly string[];
  config: Loadable<Config>;
  reloadConfig: () => void;
  roster: Loadable<User[]>;
  userName: (id: string | null | undefined) => string | null;
  agents: Loadable<Agent[]>;
  me: Agent | null;
  setAvailability: (availability: Agent['availability'], reason?: string) => Promise<ErrorView | null>;
  numbers: Loadable<PhoneNumber[]>;
  calls: CallMap;
  liveCalls: Call[];
  locallyEnded?: ReadonlySet<string>;
  upsert: (call: Call) => void;
  refetchCall: (id: string) => Promise<void>;
  history: { ids: string[]; nextCursor: string | null; loading: boolean; error: ErrorView | null; filter: HistoryFilter; loaded: boolean };
  loadHistory: (filter?: HistoryFilter) => void;
  loadMoreHistory: () => void;
  commands: CommandController;
  commandsVersion: number;
  stream: StreamStatus;
  reconnectStream: () => void;
  /** This browser's device, with any issued SIP credentials removed. */
  device: DeviceView | null;
  deviceStatus: 'none' | 'checking' | 'creating' | 'present' | 'error';
  deviceError: ErrorView | null;
  setupDevice: () => Promise<void>;
  forgetDevice: () => Promise<void>;
  mic: MicPermission;
  requestMic: () => Promise<void>;
  bridge: BridgeSnapshot;
  phone: {
    retry: () => void;
    useHere: () => void;
    turnOff: () => Promise<void>;
    turnOn: () => void;
    selectInput: (id: string | null) => Promise<string | null>;
    selectOutput: (id: string | null) => Promise<string | null>;
    resumeAudio: () => Promise<void>;
  };
  callControl: {
    engineCall: (call: Call) => BridgeCall | null;
    pending: (call: Call) => boolean;
    status: (call: Call) => StatusView | null;
    act: (call: Call, action: ActionName, digits?: string) => Promise<void>;
    setMuted: (call: Call, muted: boolean) => void;
    place: (request: PlaceCallRequest) => Promise<string | null>;
    /** Engine outbound calls the server has not confirmed yet (no call ID). */
    starting: BridgeCall[];
    /** The latest outbound start that failed before the server created a call. */
    failedStart: { key: string; message: string } | null;
    dismissFailedStart: () => void;
    cancelStarting: (key: string) => Promise<void>;
    checkAgain: (call: Call) => void;
    dismiss: (call: Call) => void;
  };
  readiness: Readiness;
  /** Profile ownership, including proven app/tab metadata. Missing only in alternative hosts/tests. */
  phoneOwnership?: OwnershipSnapshot;
  cancelDial?: () => void;
  /** Access generation for recordings and transcripts; views clear and reload when it moves. */
  mediaAccess: MediaAccessSnapshot;
  mediaAccessStore: MediaAccess;
  /** Shared per-call recording consent/capture state (one controller per call). */
  captures: CaptureRegistry;
  online: boolean;
  /** Desktop local capture state for the Spool panel; null outside the desktop shell. */
  localCapture?: LocalCaptureStore | null;
  /** A prefill for manual links (`origin: 'link'`); a reserved sequential calling-session attempt (`origin: 'session'`) auto-dials once the authenticated phone is ready. */
  dialRequest: { draft: DestinationDraft; contactId: string | null; nonce: number; origin: 'app' | 'link' | 'session'; expiresAt?: number; session?: { attemptId: string; candidateId: number; controllerId?: string } } | null;
  requestDial: (draft: DestinationDraft, contactId?: string | null, origin?: 'app' | 'link' | 'session', expiresAt?: number, session?: { attemptId: string; candidateId: number; controllerId?: string }) => void;
  startViewAs?: (userId: string) => Promise<ErrorView | null>;
  stopViewAs?: () => Promise<ErrorView | null>;
}

/** Exported for render tests and alternative hosts; the app uses WorkspaceProvider. */
export const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('useWorkspace outside WorkspaceProvider');
  return value;
}

// Only opaque identifiers and an opt-in/opt-out flag are stored; SIP credentials stay in memory.
const deviceKey = (tenantId: string, userId: string) => `sidetone.device.${tenantId}.${userId}`;
const optInKey = (tenantId: string, userId: string) => `sidetone.phone-enabled.${tenantId}.${userId}`;
const optOutKey = (tenantId: string, userId: string) => `sidetone.phone-disabled.${tenantId}.${userId}`;

export interface PhoneLifecycle {
  stop: () => Promise<void>;
  resume: () => void;
}

export interface BridgeContext {
  api: ApiClient;
  userId: string;
  capabilities: () => Readonly<Record<string, Capability>> | null;
  onResync: () => void;
  onRevoked: () => void;
}
const emptyLoadable = <T,>(): Loadable<T> => ({ value: null, error: null, loading: true });

export const AGENTS_VISIBLE_POLL_MS = 25_000;
export const AGENTS_COALESCE_MS = 2_500;
export const AGENTS_POST_READY_DELAY_MS = 4_000;

export function sanitizePhoneOwnership(raw: OwnershipSnapshot, expectedAccount: string): { snapshot: OwnershipSnapshot; crossAccountOwner: boolean } {
  const crossAccountOwner = Boolean(raw.owner && raw.owner.account !== expectedAccount);
  if (!crossAccountOwner) return { snapshot: raw, crossAccountOwner: false };
  return {
    snapshot: {
      status: raw.status,
      owner: null,
      message: raw.message ?? 'Another Sidetone account is using the phone in this browser profile.',
    },
    crossAccountOwner: true,
  };
}

export interface AgentsPresenceSyncOptions {
  api: ApiClient;
  userId: string;
  canWrite: () => boolean;
  isOnline?: () => boolean;
  isVisible?: () => boolean;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (id: ReturnType<typeof setTimeout>) => void;
  setIntervalTimer?: (fn: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearIntervalTimer?: (id: ReturnType<typeof setInterval>) => void;
  onChange: (next: Loadable<Agent[]>) => void;
}

export class AgentsPresenceSync {
  private state: Loadable<Agent[]> = emptyLoadable();
  private active = true;
  private inFlight = false;
  private pendingReload = false;
  private requestSeq = 0;
  private mutationEpoch = 0;
  private activeMutations = 0;
  private lastFetchStartedAt = 0;
  private prevReadinessKey: string | null = null;
  private postReadyTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly options: AgentsPresenceSyncOptions) {}

  getSnapshot(): Loadable<Agent[]> {
    return this.state;
  }

  private now(): number {
    return this.options.now ? this.options.now() : Date.now();
  }

  private isOnline(): boolean {
    return this.options.isOnline ? this.options.isOnline() : true;
  }

  private isVisible(): boolean {
    return this.options.isVisible ? this.options.isVisible() : true;
  }

  private setTimer(fn: () => void, ms: number): ReturnType<typeof setTimeout> {
    return (this.options.setTimer ?? setTimeout)(fn, ms);
  }

  private clearTimer(id: ReturnType<typeof setTimeout>): void {
    (this.options.clearTimer ?? clearTimeout)(id);
  }

  private setIntervalTimer(fn: () => void, ms: number): ReturnType<typeof setInterval> {
    return (this.options.setIntervalTimer ?? setInterval)(fn, ms);
  }

  private clearIntervalTimer(id: ReturnType<typeof setInterval>): void {
    (this.options.clearIntervalTimer ?? clearInterval)(id);
  }

  private setState(updater: Loadable<Agent[]> | ((prev: Loadable<Agent[]>) => Loadable<Agent[]>)): void {
    if (!this.active) return;
    this.state = typeof updater === 'function' ? updater(this.state) : updater;
    this.options.onChange(this.state);
  }

  resume(): void {
    this.active = true;
  }

  stop(): void {
    this.active = false;
    this.pendingReload = false;
    this.stopPolling();
  }

  reload(force = false): void {
    if (!this.active) return;
    if (this.inFlight && !force) {
      this.pendingReload = true;
      return;
    }
    this.inFlight = true;
    this.pendingReload = false;
    const seq = ++this.requestSeq;
    const epochAtStart = this.mutationEpoch;
    this.lastFetchStartedAt = this.now();
    this.options.api.request<CollectionEnvelope<Agent>>('/agents').then(
      env => {
        if (!this.active || seq < this.requestSeq) return;
        const mutatedDuringFetch = this.activeMutations > 0 || this.mutationEpoch !== epochAtStart;
        this.setState(prev => ({
          value: mergeAgentsProjection(prev.value, env.data, {
            selfUserId: this.options.userId,
            preserveSelfAvailability: mutatedDuringFetch,
          }),
          error: null,
          loading: false,
        }));
      },
      error => {
        if (!this.active || seq < this.requestSeq) return;
        this.setState(prev => ({
          value: prev.value,
          error: describeError(error, "Couldn't load availability"),
          loading: false,
        }));
      },
    ).finally(() => {
      if (seq === this.requestSeq) this.inFlight = false;
      if (this.active && this.pendingReload && !this.inFlight) {
        this.pendingReload = false;
        this.reload();
      }
    });
  }

  reloadIfStale(minAgeMs = AGENTS_COALESCE_MS): void {
    if (!this.active || !this.isOnline() || !this.isVisible()) return;
    if (this.inFlight) return;
    if (this.lastFetchStartedAt > 0 && this.now() - this.lastFetchStartedAt < minAgeMs) return;
    this.reload();
  }

  onReadinessTransition(key: string, isReady: boolean): void {
    if (!this.active) return;
    if (this.prevReadinessKey === null) {
      this.prevReadinessKey = key;
      return;
    }
    if (this.prevReadinessKey === key) return;
    this.prevReadinessKey = key;
    if (this.postReadyTimer !== null) {
      this.clearTimer(this.postReadyTimer);
      this.postReadyTimer = null;
    }
    if (!this.isOnline()) return;
    this.reload();
    if (isReady) {
      this.postReadyTimer = this.setTimer(() => {
        this.postReadyTimer = null;
        if (this.active && this.isOnline() && this.isVisible()) {
          this.reload();
        }
      }, AGENTS_POST_READY_DELAY_MS);
    }
  }

  startPolling(pollMs = AGENTS_VISIBLE_POLL_MS): void {
    if (!this.active || this.pollTimer !== null) return;
    this.pollTimer = this.setIntervalTimer(() => {
      this.reloadIfStale(Math.max(AGENTS_COALESCE_MS, pollMs - 1000));
    }, pollMs);
  }

  stopPolling(): void {
    if (this.pollTimer !== null) {
      this.clearIntervalTimer(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.postReadyTimer !== null) {
      this.clearTimer(this.postReadyTimer);
      this.postReadyTimer = null;
    }
  }

  async setAvailability(availability: Agent['availability'], reason?: string): Promise<ErrorView | null> {
    if (!this.options.canWrite()) {
      return { title: 'You cannot change availability in this view.', detail: '', code: 'forbidden', requestId: null, retryable: false };
    }
    const me = this.state.value?.find(a => a.user_id === this.options.userId) ?? null;
    if (!me) {
      return { title: 'Your availability record is not loaded yet.', detail: '', code: 'not_loaded', requestId: null, retryable: true };
    }
    this.mutationEpoch++;
    this.activeMutations++;
    try {
      const env = await this.options.api.send<DataEnvelope<Agent>>('PATCH', '/agents/me/availability', {
        availability,
        ...(reason ? { reason } : {}),
        expected_version: me.version,
      });
      this.mutationEpoch++;
      this.setState(prev => ({
        ...prev,
        value: (prev.value ?? []).map(a => (
          a.user_id === this.options.userId && env.data.version >= a.version ? env.data : a
        )),
      }));
      return null;
    } catch (error) {
      if (isApiRequestError(error) && error.status === 409) this.reload(true);
      return describeError(error, "Couldn't change your status");
    } finally {
      this.activeMutations = Math.max(0, this.activeMutations - 1);
    }
  }
}

export function WorkspaceProvider({ api: sourceApi, session, children, onSessionExpired, onLiveCallChange, bridgeFactory, registerTeardown, refreshSession, onPolicyChanged, sensitiveBlocked = false, startViewAs, stopViewAs }: {
  api: ApiClient;
  session: Session;
  children: ReactNode;
  onSessionExpired: () => void;
  onLiveCallChange: (live: boolean) => void;
  /** Re-reads the session after a media-access change; true when the same session is still valid. */
  refreshSession?: () => Promise<boolean>;
  /** The server's policy no longer accepts this session (for example MFA): require a fresh sign-in. */
  onPolicyChanged?: (reason: string | null) => void;
  /** The app session expired while a call keeps the workspace open: hide all sensitive media. */
  sensitiveBlocked?: boolean;
  /** Lets the shell stop the phone (unregister, release the microphone) before sign-out or a workspace switch. */
  registerTeardown?: (phone: PhoneLifecycle | null) => void;
  bridgeFactory?: (context: BridgeContext) => CallBridge;
  startViewAs?: (userId: string) => Promise<ErrorView | null>;
  stopViewAs?: () => Promise<ErrorView | null>;
}) {
  const projectionOrder = useRef(new CallProjectionOrder()).current;
  const api = useMemo(() => projectionOrder.client(sourceApi), [sourceApi, projectionOrder]);
  const viewingAs = session.view_as ?? null;
  const userId = viewingAs?.user_id ?? session.user?.id ?? '';
  const tenantId = session.tenant?.id ?? '';
  const permissions = session.permissions;
  const { announce } = useAnnouncer();
  const desktop = useDesktop();

  const [config, setConfig] = useState<Loadable<Config>>(emptyLoadable);
  const [roster, setRoster] = useState<Loadable<User[]>>(emptyLoadable);
  const [agents, setAgents] = useState<Loadable<Agent[]>>(emptyLoadable);
  const [numbers, setNumbers] = useState<Loadable<PhoneNumber[]>>(emptyLoadable);
  const [calls, setCalls] = useState<CallMap>(() => new Map());
  const [locallyEnded, setLocallyEnded] = useState<ReadonlySet<string>>(() => new Set());
  const callsRef = useRef<CallMap>(calls);
  callsRef.current = calls;
  const [stream, setStream] = useState<StreamStatus>({ state: 'idle', attempt: 0, nextRetryAt: null, lastEventAt: null, replayed: false });
  const streamRef = useRef<EventStream | null>(null);
  const [device, setDevice] = useState<DeviceView | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<WorkspaceValue['deviceStatus']>('checking');
  const [deviceError, setDeviceError] = useState<ErrorView | null>(null);
  const [localMic, setLocalMic] = useState<MicPermission>('unknown');
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [commandsVersion, setCommandsVersion] = useState(0);
  const [dialRequest, setDialRequest] = useState<WorkspaceValue['dialRequest']>(null);

  const configRef = useRef<Config | null>(null);
  configRef.current = config.value;
  const bridgeEvents = useRef({ resync: () => {}, revoked: () => {} });
  const bridgeRef = useRef<CallBridge | null>(null);
  if (!bridgeRef.current) {
    bridgeRef.current = bridgeFactory && !viewingAs
      ? bridgeFactory({
          api,
          userId,
          capabilities: () => configRef.current?.capabilities ?? null,
          onResync: () => bridgeEvents.current.resync(),
          onRevoked: () => bridgeEvents.current.revoked(),
        })
      : new UnconfiguredBridge();
  }
  const bridgeObj = bridgeRef.current;
  const bridge = useSyncExternalStore(
    useCallback(l => bridgeObj.subscribe(l), [bridgeObj]),
    () => bridgeObj.getSnapshot(),
    () => bridgeObj.getSnapshot(),
  );
  const placing = useRef(false);
  const deviceMutation = useRef<Promise<unknown> | null>(null);
  const ownership = useMemo(() => browserPhoneOwnership(`${tenantId}:${userId}`,
    () => placing.current || liveCallsFor(callsRef.current, userId).length > 0 || bridgeObj.getSnapshot().calls.some(c => c.phase !== 'ended' && c.phase !== 'failed'),
    async handoff => {
      await deviceMutation.current?.catch(() => undefined);
      // A verified incoming offer may arrive while a device operation was settling.
      if (handoff && (placing.current || liveCallsFor(callsRef.current, userId).length > 0 || bridgeObj.getSnapshot().calls.some(c => c.phase !== 'ended' && c.phase !== 'failed'))) return false;
      await bridgeObj.stop();
      if (bridgeObj.releaseDevice) await bridgeObj.releaseDevice();
      else bridgeObj.attachDevice(null);
    }), [tenantId, userId, bridgeObj]);
  const rawPhoneOwnership = useSyncExternalStore(ownership.subscribe, ownership.getSnapshot, ownership.getSnapshot);
  const { snapshot: phoneOwnership, crossAccountOwner } = useMemo(
    () => sanitizePhoneOwnership(rawPhoneOwnership, `${tenantId}:${userId}`),
    [rawPhoneOwnership, tenantId, userId],
  );
  useEffect(() => {
    const ownerState = bridge.phone.state === 'registered' && !bridge.phone.acceptingCalls ? 'stopped' : bridge.phone.state;
    ownership.update(ownerState);
  }, [ownership, bridge.phone.state, bridge.phone.acceptingCalls, bridge.calls, calls]);

  const capturesRef = useRef<CaptureRegistry | null>(null);
  const upsert = useCallback((call: Call) => {
    const next = projectionOrder.upsert(callsRef.current, call);
    callsRef.current = next;
    setCalls(next);
  }, [projectionOrder]);

  const refetchCall = useCallback(async (id: string) => {
    const request = projectionOrder.begin();
    try {
      const env = await api.request<DataEnvelope<CallWithContext>>(`/calls/${encodeURIComponent(id)}`);
      const accepted = projectionOrder.accepts(callsRef.current, env.data);
      upsert(env.data);
      // API-08: the pinned context card has its own version; keep the newest even when this projection lost to a newer read.
      const withCard = upsertContextCard(callsRef.current, env.data.id, env.data.context_card);
      if (withCard !== callsRef.current) { callsRef.current = withCard; setCalls(withCard); }
      // A current identical projection still reconciles the engine; a stale HTTP
      // result must reach neither the cache nor the engine.
      if (accepted) bridgeObj.applyServerCall(env.data);
    } catch (error) {
      if (isApiRequestError(error) && error.status === 404 && projectionOrder.current(id, request)) {
        projectionOrder.removed(id, request);
        const next = new Map(callsRef.current);
        next.delete(id);
        callsRef.current = next;
        setCalls(next);
      }
    }
  }, [api, upsert, bridgeObj, projectionOrder]);

  const commandsRef = useRef<CommandController | null>(null);
  if (!commandsRef.current) {
    commandsRef.current = new CommandController({ api, onCall: upsert, refetchCall, onChange: () => setCommandsVersion(v => v + 1) });
  }
  const commands = commandsRef.current;

  const load = useCallback(<T,>(path: string, set: (l: Loadable<T>) => void, pick: (body: unknown) => T, fallbackTitle: string) => {
    set({ value: null, error: null, loading: true });
    api.request<unknown>(path).then(
      body => set({ value: pick(body), error: null, loading: false }),
      error => {
        if (!isAbortError(error)) set({ value: null, error: describeError(error, fallbackTitle), loading: false });
      },
    );
  }, [api]);

  // A reload (after a reconnect, a capability event or an access change) keeps the settings on screen
  // until the new ones arrive. Clearing them first blanked every capability-gated part of the app, the
  // status bar and the fixture banner for the length of the request, and the whole layout jumped.
  const reloadConfig = useCallback(() => {
    setConfig(prev => (prev.value ? { value: prev.value, error: null, loading: true } : { value: null, error: null, loading: true }));
    api.request<DataEnvelope<Config>>('/config').then(
      body => setConfig({ value: body.data, error: null, loading: false }),
      error => {
        if (!isAbortError(error)) setConfig({ value: null, error: describeError(error, "Couldn't load workspace settings"), loading: false });
      },
    );
  }, [api]);
  const reloadNumbers = useCallback(() => {
    api.request<CollectionEnvelope<PhoneNumber>>('/numbers').then(
      body => setNumbers({ value: body.data, error: null, loading: false }),
      () => undefined,
    );
  }, [api]);
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const canWritePresenceRef = useRef(false);
  canWritePresenceRef.current = hasPermission(permissions, 'presence:write_own') && !viewingAs;
  const agentsSync = useMemo(() => new AgentsPresenceSync({
    api,
    userId,
    canWrite: () => canWritePresenceRef.current,
    isOnline: () => onlineRef.current,
    isVisible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
    onChange: setAgents,
  }), [api, userId]);
  const reloadAgents = useCallback(() => {
    agentsSync.reload();
  }, [agentsSync]);

  const {history,loadHistory,loadMoreHistory}=useCallHistory(api,{
    onCalls: rows=>{for(const call of rows)upsert(call);},
    onPage: env=>{if(!streamRef.current)startStream(env.meta.event_cursor??null);},
    onError: error=>{if(!streamRef.current&&isApiRequestError(error)&&error.status===403)startStream(null);},
  });

  const onEvent = useCallback((event: EventEnvelope) => {
    bridgeObj.notifyServerEvent(event);
    notifyTranscriptEvent(event);
    // Consent/capture changes and call changes (observed recording state) refresh that call's capture view.
    if (event.aggregate.type === 'call_capture' || event.aggregate.type === 'call') capturesRef.current?.notify(event.aggregate.id);
    const decision = decideEvent(callsRef.current, event);
    if (decision.kind === 'refetch_call') {
      // Re-evaluate membership at the server under the current filter. A completion
      // can enter one outcome and leave another; a detail fetch alone cannot decide it.
      loadHistory();
      reloadNumbers();
      // Detail is the last-issued projection when both reads follow one event.
      void refetchCall(decision.callId);
    } else if (decision.kind === 'refetch_agents') reloadAgents();
    else if (decision.kind === 'refetch_config') { reloadConfig(); reloadNumbers(); }
  }, [refetchCall, reloadAgents, reloadConfig, reloadNumbers, bridgeObj, loadHistory]);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const resyncRef = useRef<() => void>(() => undefined);
  resyncRef.current = () => {
    reloadConfig();
    reloadAgents();
    reloadNumbers();
    loadHistory();
    notifyTranscriptResync();
    for (const call of liveCallsFor(callsRef.current, userId)) void refetchCall(call.id);
    announce('Refreshed after reconnecting');
  };
  const expiredRef = useRef(onSessionExpired);
  expiredRef.current = onSessionExpired;

  // Sensitive media access: control events clear views before any network work.
  const mediaAccessStore = useMemo(() => new MediaAccess(), []);
  const mediaAccess = useSyncExternalStore(mediaAccessStore.subscribe, mediaAccessStore.getSnapshot, mediaAccessStore.getSnapshot);
  const captures = useMemo(() => new CaptureRegistry(callId => new CaptureController({ api, callId, selfUserId: userId, access: mediaAccessStore })), [api, userId, mediaAccessStore]);
  capturesRef.current = captures;
  const controlDeps = useRef({ refreshSession, onPolicyChanged, reloadConfig });
  controlDeps.current = { refreshSession, onPolicyChanged, reloadConfig };
  const handleControl = useMemo(() => createControlHandler({
    access: mediaAccessStore,
    refreshSession: () => controlDeps.current.refreshSession?.() ?? Promise.resolve(false),
    reloadConfig: () => controlDeps.current.reloadConfig(),
    onPolicyChanged: reason => controlDeps.current.onPolicyChanged?.(reason),
  }), [mediaAccessStore]);

  useEffect(() => {
    if (sensitiveBlocked) mediaAccessStore.invalidate('blocked', 'session_expired');
    else if (mediaAccessStore.getSnapshot().reason === 'session_expired') mediaAccessStore.ready();
  }, [sensitiveBlocked, mediaAccessStore]);

  // A changed media version or grant set (re-sign-in, another tab, a refresh) is a new generation.
  const mediaKey = `${session.media_access_version ?? 1}|${hasPermission(permissions, 'transcripts:read')}|${hasPermission(permissions, 'recordings:read')}`;
  const lastMediaKey = useRef(mediaKey);
  useEffect(() => {
    if (lastMediaKey.current === mediaKey) return;
    lastMediaKey.current = mediaKey;
    if (mediaAccessStore.getSnapshot().state === 'ready') mediaAccessStore.ready();
  }, [mediaKey, mediaAccessStore]);

  const startStream = useCallback((cursor: string | null) => {
    if (streamRef.current) return;
    const s = new EventStream({
      url: api.url('/events'),
      onEvent: e => onEventRef.current(e),
      onResync: () => resyncRef.current(),
      onSessionExpired: () => expiredRef.current(),
      onControl: control => { void handleControl(control); },
      onStatus: setStream,
    });
    streamRef.current = s;
    s.start(cursor);
  }, [api, handleControl]);

  // Initial loads for this tenant. Disposal is deferred one tick so React StrictMode's
  // development-only mount/unmount/mount cycle does not dispose the live bridge.
  const teardownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (teardownTimer.current) { clearTimeout(teardownTimer.current); teardownTimer.current = null; }
    agentsSync.resume();
    ownership.start();
    registerTeardown?.({ stop: () => ownership.stop(), resume: () => { ownership.start(); } });
    reloadConfig();
    reloadAgents();
    if (hasPermission(permissions, 'users:read')) load<User[]>('/users', setRoster, b => (b as CollectionEnvelope<User>).data, "Couldn't load your team");
    else setRoster({ value: null, error: null, loading: false });
    load<PhoneNumber[]>('/numbers', setNumbers, b => (b as CollectionEnvelope<PhoneNumber>).data, "Couldn't load phone numbers");
    loadHistory({ direction: '', state: '' });
    return () => {
      agentsSync.stopPolling();
      streamRef.current?.stop();
      streamRef.current = null;
      registerTeardown?.(null);
      teardownTimer.current = setTimeout(() => {
        agentsSync.stop();
        commands.dispose();
        capturesRef.current?.disposeAll();
        void ownership.stop().finally(() => bridgeObj.dispose());
      }, 0);
    };
    // Run once per provider instance (keyed by tenant+user).
  }, []);

  // Network reachability drives the stream and the phone state.
  useEffect(() => {
    const up = () => { setOnline(true); streamRef.current?.setOnline(true); };
    const down = () => { setOnline(false); streamRef.current?.setOnline(false); };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  // Microphone permission state, without prompting. The engine reports its own once it runs.
  useEffect(() => {
    let status: PermissionStatus | null = null;
    const onChange = () => status && setLocalMic(mapPermission(status.state));
    if (!navigator.mediaDevices?.getUserMedia) {
      setLocalMic('unsupported');
      return;
    }
    navigator.permissions?.query({ name: 'microphone' as PermissionName }).then(
      s => { status = s; setLocalMic(mapPermission(s.state)); s.addEventListener('change', onChange); },
      () => setLocalMic('unknown'),
    );
    return () => status?.removeEventListener('change', onChange);
  }, []);
  const engineActive = bridge.engine === 'call-client' && bridge.phone.state !== 'unavailable';
  const mic: MicPermission = engineActive && bridge.media.permission !== 'unknown' ? bridge.media.permission : localMic;

  /** The iPhone engine retains a muted source until the phone is turned off or signed out. */
  const requestMic = useCallback(async () => {
    setDeviceError(null);
    // Desktop: the OS-level microphone prompt (macOS) comes first; a refusal there blocks the browser API too.
    if (desktop) {
      const access = await desktop.devices.askMicrophone().catch(() => 'unknown' as const);
      if (access === 'denied' || access === 'restricted') {
        setDeviceError({ title: 'Microphone access is blocked in your system settings. Allow Sidetone to use the microphone, then try again.', detail: '', code: `os_microphone_${access}`, requestId: null, retryable: true });
        return;
      }
    }
    if (engineActive) {
      const result = await bridgeObj.requestMicrophone();
      if (!result.ok) setDeviceError({ title: engineErrorText(result.code), detail: '', code: result.code, requestId: null, retryable: true });
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) { setLocalMic('unsupported'); return; }
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of media.getTracks()) track.stop();
      setLocalMic('granted');
    } catch (error) {
      const name = (error as { name?: string }).name;
      setLocalMic(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unknown');
      if (name === 'NotFoundError') setDeviceError({ title: engineErrorText('microphone_not_found'), detail: '', code: 'microphone_not_found', requestId: null, retryable: true });
      else if (name === 'NotReadableError') setDeviceError({ title: engineErrorText('microphone_in_use'), detail: '', code: 'microphone_in_use', requestId: null, retryable: true });
    }
  }, [bridgeObj, engineActive, desktop]);

  const automaticMicAttempted = useRef(false);
  useEffect(() => {
    if (!engineActive || desktop || automaticMicAttempted.current || !(iphoneSafari() || iphoneHomeScreen())) return;
    return onFirstPhoneGesture(document, () => {
      automaticMicAttempted.current = true;
      void requestMic();
    });
  }, [engineActive, desktop, requestMic]);

  const storage = useMemo(() => ({
    deviceId: () => safeStorage()?.getItem(deviceKey(tenantId, userId)) ?? null,
    optedIn: () => safeStorage()?.getItem(optOutKey(tenantId, userId)) !== '1',
    remember: (id: string) => { safeStorage()?.setItem(deviceKey(tenantId, userId), id); safeStorage()?.setItem(optInKey(tenantId, userId), '1'); safeStorage()?.removeItem(optOutKey(tenantId, userId)); },
    forgetId: () => safeStorage()?.removeItem(deviceKey(tenantId, userId)),
    forgetAll: () => { safeStorage()?.removeItem(deviceKey(tenantId, userId)); safeStorage()?.removeItem(optInKey(tenantId, userId)); safeStorage()?.setItem(optOutKey(tenantId, userId), '1'); },
  }), [tenantId, userId]);

  const autoReprovisioned = useRef(false);

  /** Server device -> bridge (with credentials) and UI state (without them). */
  const adoptDevice = useCallback((full: Device | null) => {
    if (!ownership.isOwner()) return;
    if (full && !full.revoked_at) autoReprovisioned.current = false;
    bridgeObj.attachDevice(full);
    if (full) bridgeObj.start();
    setDevice(full ? redactDevice(full) : null);
    setDeviceStatus(full ? 'present' : 'none');
  }, [bridgeObj, ownership]);

  const heartbeat = useCallback(async (id: string): Promise<Device | null> => {
    if (!ownership.isOwner()) return null;
    const body = { registration_state: 'unregistered', audio_permission: localMic === 'granted' ? 'granted' : localMic === 'denied' ? 'denied' : 'unknown', client_version: CLIENT_VERSION };
    try {
      return (await api.send<DataEnvelope<Device>>('POST', `/devices/${encodeURIComponent(id)}/heartbeat`, body)).data;
    } catch (error) {
      if (isApiRequestError(error) && error.status === 404) return null;
      throw error;
    }
  }, [api, localMic, ownership]);

  const createDevice = useCallback(async (automatic: boolean) => {
    if (!ownership.isOwner()) return;
    setDeviceStatus('creating');
    setDeviceError(null);
    try {
      const env = await api.send<DataEnvelope<Device>>('POST', '/devices', { label: 'Web browser', platform: 'web', client_version: CLIENT_VERSION });
      storage.remember(env.data.id);
      adoptDevice(env.data);
      if (!automatic) announce('This browser is set up as your phone.');
    } catch (error) {
      setDeviceStatus('error');
      setDeviceError(describeError(error, "Couldn't set up this browser as your phone"));
    }
  }, [api, announce, adoptDevice, storage, ownership]);

  // Reuse this session's device if it still exists. A new session (sign-in, workspace switch)
  // revokes old devices server-side, so a user who opted in gets a fresh device automatically.
  const deviceInit = useRef(false);
  useEffect(() => {
    if (phoneOwnership.status !== 'owner') {
      deviceInit.current = false;
      setDevice(null);
      setDeviceStatus('none');
      return;
    }
    if (deviceInit.current) return;
    deviceInit.current = true;
    if (viewingAs || !hasPermission(permissions, 'devices:manage_own')) { setDeviceStatus('none'); return; }
    const stored = storage.deviceId();
    const optedIn = storage.optedIn() || !!stored;
    if (!stored) {
      if (optedIn) deviceMutation.current = createDevice(true);
      else setDeviceStatus('none');
      return;
    }
    deviceMutation.current = heartbeat(stored).then(
      d => {
        if (d) adoptDevice(d);
        else {
          storage.forgetId();
          if (optedIn) return createDevice(true);
          else setDeviceStatus('none');
        }
      },
      error => { setDeviceStatus('error'); setDeviceError(describeError(error, "Couldn't check this browser's phone")); },
    );
    // Once per provider instance.
  }, [phoneOwnership.status, ownership]);

  // Without an audio engine, keep the device's server TTL fresh here. With an engine, the
  // engine sends heartbeats carrying its real registration state.
  useEffect(() => {
    if (!ownership.isOwner() || !device || device.revoked_at || engineActive) return;
    const timer = setInterval(() => {
      deviceMutation.current = heartbeat(device.id).then(d => { if (!d && ownership.isOwner()) { storage.forgetId(); adoptDevice(null); } }, () => undefined);
    }, 20000);
    return () => clearInterval(timer);
  }, [device, engineActive, heartbeat, adoptDevice, storage, ownership, phoneOwnership.status]);

  const setupDevice = useCallback(() => { const task = createDevice(false); deviceMutation.current = task; return task; }, [createDevice]);

  const forgetDevice = useCallback(async () => {
    if (!device || !ownership.isOwner()) return;
    // Stop the engine first so it unregisters while its credentials are still valid.
    const task = (async () => { try {
      await bridgeObj.stop();
      if (bridgeObj.releaseDevice) await bridgeObj.releaseDevice();
      else bridgeObj.attachDevice(null);
      await api.request<void>(`/devices/${encodeURIComponent(device.id)}`, { method: 'DELETE' });
    } catch (error) {
      if (!(isApiRequestError(error) && error.status === 404)) setDeviceError(describeError(error, "Couldn't remove this browser"));
    }
    storage.forgetAll();
    setDevice(null);
    setDeviceStatus('none');
    })();
    deviceMutation.current = task;
    await task;
  }, [api, bridgeObj, device, storage, ownership]);

  bridgeEvents.current.revoked = () => {
    storage.forgetId();
    setDevice(null);
    if (!autoReprovisioned.current && ownership.isOwner() && storage.optedIn() && !viewingAs && hasPermission(permissions, 'devices:manage_own')) {
      autoReprovisioned.current = true;
      deviceMutation.current = createDevice(true);
      return;
    }
    setDeviceStatus('none');
    setDeviceError({ title: 'This browser was removed as a phone. Set it up again to take calls here.', detail: '', code: 'device_revoked', requestId: null, retryable: false });
    announce('This browser was removed as a phone.');
  };
  bridgeEvents.current.resync = () => resyncRef.current();

  // Calls the engine verified (incoming offers, outbound receipts) seed the shared cache.
  useEffect(() => {
    for (const c of bridge.calls) if (c.server) upsert(c.server);
  }, [bridge.calls, upsert]);

  const me = useMemo(() => agents.value?.find(a => a.user_id === userId) ?? null, [agents.value, userId]);

  const setAvailability = useCallback(
    (availability: Agent['availability'], reason?: string): Promise<ErrorView | null> =>
      agentsSync.setAvailability(availability, reason),
    [agentsSync],
  );

  const liveCalls = useMemo(() => liveCallsFor(calls, userId).filter(c => !locallyEnded.has(c.id) && !bridge.calls.some(e => e.callId === c.id && e.phase === 'ended' && ['local_hangup', 'declined'].includes(e.endReason ?? ''))), [calls, userId, locallyEnded, bridge.calls]);
  const hasLive = liveCalls.length > 0;
  useEffect(() => { onLiveCallChange(hasLive); }, [hasLive, onLiveCallChange]);

  const readiness = useMemo<Readiness>(() => phoneOwnership.status === 'observer' ? (
    crossAccountOwner ? {
      state: 'unconfigured',
      label: 'In another window',
      reason: 'Another Sidetone account is using the phone in this browser profile.',
      action: null,
      diagnostic: { code: 'phone_active_for_another_account', detail: null },
    } : {
      state: 'in_another_tab', label: phoneOwnership.owner?.kind === 'app' ? 'In Sidetone app' : 'In another window',
      reason: phoneOwnership.owner?.kind === 'app' ? 'Your phone is in the Sidetone app.' : 'Your phone is open in another window in this browser profile.',
      action: 'use_here', diagnostic: null,
    }
  ) : phoneOwnership.status === 'unsupported' ? { state: 'unsupported', label: 'Not supported', reason: phoneOwnership.message ?? 'Use Chrome or Edge to connect the phone.', action: null, diagnostic: null } : phoneReadiness({
    online,
    callingInternal: capability(config.value, 'calling.internal'),
    canManageDevices: hasPermission(permissions, 'devices:manage_own'),
    device,
    mic,
    bridge,
  }), [online, config.value, permissions, device, mic, bridge, phoneOwnership, crossAccountOwner]);

  const readinessTransitionKey = `${readiness.state}|${bridge.phone.state}|${bridge.phone.acceptingCalls}|${phoneOwnership.status}|${phoneOwnership.owner?.state ?? ''}`;
  useEffect(() => {
    agentsSync.onReadinessTransition(
      readinessTransitionKey,
      readiness.state === 'ready' || bridge.phone.state === 'registered',
    );
  }, [agentsSync, readinessTransitionKey, readiness.state, bridge.phone.state]);

  useEffect(() => {
    if (!online || typeof window === 'undefined') {
      agentsSync.stopPolling();
      return;
    }
    const onFocus = () => {
      agentsSync.reloadIfStale(AGENTS_COALESCE_MS);
      reloadNumbers();
    };
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        agentsSync.reloadIfStale(AGENTS_COALESCE_MS);
        reloadNumbers();
      }
    };
    window.addEventListener('focus', onFocus);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    agentsSync.startPolling(AGENTS_VISIBLE_POLL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      agentsSync.stopPolling();
    };
  }, [online, agentsSync, reloadNumbers]);

  // Desktop shell only: feed the local capture producer server-verified state and engine tracks.
  const localCapture = useDesktopCapture({
    desktop,
    bridge: bridgeObj,
    captures,
    tenantId,
    userId,
    calls,
    liveCalls,
    capability: capability(config.value, 'desktop.capture'),
    verified: stream.state === 'live' && mediaAccess.state === 'ready',
  });

  const rosterById = useMemo(() => new Map((roster.value ?? []).map(u => [u.id, u])), [roster.value]);
  const userName = useCallback((id: string | null | undefined) => {
    if (!id) return null;
    if (id === userId) return 'You';
    return rosterById.get(id)?.display_name ?? null;
  }, [rosterById, userId]);

  const requestDial = useCallback((draft: DestinationDraft, contactId: string | null = null, origin: 'app' | 'link' | 'session' = 'app', expiresAt?: number, session?: { attemptId: string; candidateId: number; controllerId?: string }) => {
    setDialRequest({ draft, contactId, nonce: Date.now(), origin, ...(expiresAt ? { expiresAt } : {}), ...(session ? { session } : {}) });
  }, []);

  const callControl = useMemo<WorkspaceValue['callControl']>(() => {
    const setError = (id: string, message: string | null) => setActionErrors(prev => {
      if (!message && !(id in prev)) return prev;
      const next = { ...prev };
      if (message) next[id] = message;
      else delete next[id];
      return next;
    });
    return {
      engineCall: call => liveEngineCall(bridge, call),
      pending: call => isPending(bridge, commands, call),
      status: call => statusFor(bridge, commands, call, actionErrors[call.id] ?? null),
      act: async (call, action, digits) => {
        if (action !== 'hangup' && action !== 'reject' && !ownership.isOwner()) { setError(call.id, 'Open the window that owns your phone to control this call.'); return; }
        setError(call.id, null);
        if (action === 'hangup' || action === 'reject') {
          // UI dismissal is local only: history and server reconciliation keep
          // the real outcome, and late SSE updates cannot reopen this card.
          setLocallyEnded(prev => new Set(prev).add(call.id));
        }
        const error = await runAction(bridgeObj, bridgeObj.getSnapshot(), commands, call, action, digits);
        if (error) setError(call.id, error);
      },
      setMuted: (call, muted) => {
        const ec = liveEngineCall(bridgeObj.getSnapshot(), call);
        if (!ec) return;
        const result = bridgeObj.setMuted(ec.key, muted);
        setError(call.id, result.ok ? null : engineErrorText(result.code));
      },
      place: async request => {
        if (!ownership.isOwner()) return 'Open the window that owns your phone to make this call.';
        if (placing.current) return 'A call is already being started.';
        placing.current = true;
        try {
          const result = await bridgeObj.placeCall(request);
          return result.ok ? null : engineErrorText(result.code, result.serverCode);
        } finally {
          placing.current = false;
          reloadNumbers();
        }
      },
      starting: bridge.calls.filter(c => c.origin === 'outbound' && c.callId === null && c.phase !== 'ended' && c.phase !== 'failed'),
      failedStart: (() => {
        const failed = [...bridge.calls].reverse().find(c => c.origin === 'outbound' && c.callId === null && c.phase === 'failed');
        if (!failed) return null;
        const err = failed.error ?? (failed.command?.error ? { code: 'server_rejected', serverCode: failed.command.error.code } : null);
        return { key: failed.key, message: err ? engineErrorText(err.code, err.serverCode) : 'The call couldn’t be started.' };
      })(),
      dismissFailedStart: () => {
        for (const c of bridgeObj.getSnapshot().calls) if (c.origin === 'outbound' && c.callId === null && c.phase === 'failed') bridgeObj.dismiss(c.key);
      },
      cancelStarting: async key => { await bridgeObj.hangup(key); },
      checkAgain: call => { void commands.checkAgain(call.id); },
      dismiss: call => { setError(call.id, null); commands.dismiss(call.id); },
    };
  }, [bridge, bridgeObj, commands, actionErrors, commandsVersion, ownership, reloadNumbers]);

  const phone = useMemo<WorkspaceValue['phone']>(() => ({
    retry: () => bridgeObj.retryNow(),
    useHere: () => ownership.requestHandoff(),
    turnOff: () => bridgeObj.stop(),
    turnOn: () => bridgeObj.start(),
    selectInput: async id => { const r = await bridgeObj.selectInput(id); return r.ok ? null : engineErrorText(r.code); },
    selectOutput: async id => { const r = await bridgeObj.selectOutput(id); return r.ok ? null : engineErrorText(r.code); },
    resumeAudio: () => bridgeObj.resumeAudio(),
  }), [bridgeObj, ownership]);

  const value: WorkspaceValue = {
    api, session, userId, permissions,
    config, reloadConfig,
    roster, userName,
    agents, me, setAvailability,
    numbers,
    calls, liveCalls, locallyEnded, upsert, refetchCall,
    history, loadHistory, loadMoreHistory,
    commands, commandsVersion,
    stream, reconnectStream: () => streamRef.current?.reconnectNow(),
    device, deviceStatus, deviceError, setupDevice, forgetDevice,
    mic, requestMic,
    bridge, phone, callControl,
    readiness, online, phoneOwnership, cancelDial: () => setDialRequest(null),
    mediaAccess, mediaAccessStore,
    captures,
    localCapture,
    dialRequest, requestDial,
    ...(startViewAs ? { startViewAs } : {}),
    ...(stopViewAs ? { stopViewAs } : {}),
  };
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

function mapPermission(state: PermissionState): MicPermission {
  return state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'prompt';
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export { isLive };
